import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CircleDashed,
  ExternalLink,
  FolderKanban,
  Hash,
  KeyRound,
  Mail,
  Sparkles,
  Tag,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import {
  getJiraImportJob,
  jiraListProjects,
  jiraValidate,
  startJiraImport,
  type ImportJobRow,
  type JiraCredentialsInput,
  type JiraProject,
} from '@/lib/jira-import-api';
import { cn } from '@/lib/utils';

type Step = 'connect' | 'pick' | 'configure' | 'progress';

function suggestKey(name: string): string {
  const alphaOnly = name
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 5);
  return alphaOnly.length >= 2 ? alphaOnly : alphaOnly.padEnd(2, 'X');
}

export function JiraImportDialog({
  open,
  onOpenChange,
  orgId,
  onImported,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  orgId: string;
  onImported: (projectKey: string) => void;
}) {
  const { error, success } = useToast();
  const qc = useQueryClient();

  const [step, setStep] = useState<Step>('connect');
  const [creds, setCreds] = useState<JiraCredentialsInput>({
    baseUrl: '',
    email: '',
    apiToken: '',
    authMode: 'basic',
  });
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<JiraProject | null>(null);
  const [localName, setLocalName] = useState('');
  const [localKey, setLocalKey] = useState('');
  const [localKeyDirty, setLocalKeyDirty] = useState(false);
  const [includeComments, setIncludeComments] = useState(true);
  const [jobId, setJobId] = useState<string | null>(null);

  const computedKey = localKeyDirty ? localKey : suggestKey(localName);

  // Reset state whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setStep('connect');
      setProjects([]);
      setSelectedProject(null);
      setLocalName('');
      setLocalKey('');
      setLocalKeyDirty(false);
      setJobId(null);
    }
  }, [open]);

  const validateAndList = useMutation({
    mutationFn: async () => {
      await jiraValidate(orgId, creds);
      const list = await jiraListProjects(orgId, creds);
      return list;
    },
    onSuccess: (list) => {
      setProjects(list);
      setStep('pick');
    },
    onError: (e) => error('Could not connect to Jira', extractApiErrorMessage(e)),
  });

  const start = useMutation({
    mutationFn: () => {
      if (!selectedProject) throw new Error('No project selected');
      return startJiraImport(orgId, {
        credentials: creds,
        jiraProjectId: selectedProject.id,
        jiraProjectKey: selectedProject.key,
        jiraProjectName: selectedProject.name,
        localProjectName: localName.trim(),
        localProjectKey: computedKey,
        includeComments,
      });
    },
    onSuccess: (res) => {
      setJobId(res.jobId);
      setStep('progress');
    },
    onError: (e) => error('Could not start import', extractApiErrorMessage(e)),
  });

  // Poll the job.
  const jobQuery = useQuery({
    queryKey: ['org', orgId, 'jira-import', jobId],
    queryFn: () => getJiraImportJob(orgId, jobId!),
    enabled: Boolean(jobId),
    refetchInterval: (q) => {
      const data = q.state.data as ImportJobRow | undefined;
      if (!data) return 1500;
      return data.status === 'COMPLETED' || data.status === 'FAILED' ? false : 1500;
    },
  });

  useEffect(() => {
    const job = jobQuery.data;
    if (job?.status === 'COMPLETED') {
      success('Import complete', job.project?.key ?? undefined);
      qc.invalidateQueries({ queryKey: ['org', orgId, 'projects'] });
      if (job.project?.key) onImported(job.project.key);
    }
  }, [jobQuery.data, qc, orgId, onImported, success]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-hidden p-0">
        <div className="relative border-b bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pb-5 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Sparkles className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <DialogHeader>
                <DialogTitle className="text-xl">Import from Jira</DialogTitle>
                <DialogDescription className="mt-1 text-xs">
                  Connect your Jira Cloud site and bring a project — issues, users, statuses,
                  sprints, and comments — into this workspace.
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>

          <StepBar step={step} />
        </div>

        <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
          {step === 'connect' ? (
            <ConnectStep
              creds={creds}
              setCreds={setCreds}
              busy={validateAndList.isPending}
              onSubmit={() => validateAndList.mutate()}
            />
          ) : step === 'pick' ? (
            <PickStep
              projects={projects}
              selected={selectedProject}
              onSelect={(p) => {
                setSelectedProject(p);
                if (!localName) setLocalName(p.name);
                if (!localKeyDirty) setLocalKey(suggestKey(p.name || p.key));
              }}
            />
          ) : step === 'configure' ? (
            <ConfigureStep
              project={selectedProject!}
              localName={localName}
              setLocalName={setLocalName}
              computedKey={computedKey}
              onKeyChange={(v) => {
                setLocalKey(v.toUpperCase());
                setLocalKeyDirty(true);
              }}
              includeComments={includeComments}
              setIncludeComments={setIncludeComments}
            />
          ) : (
            <ProgressStep job={jobQuery.data ?? null} loading={jobQuery.isLoading} />
          )}
        </div>

        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          {step === 'connect' ? (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={validateAndList.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => validateAndList.mutate()}
                disabled={
                  validateAndList.isPending ||
                  !creds.baseUrl.trim() ||
                  !creds.apiToken.trim() ||
                  ((creds.authMode ?? 'basic') === 'basic' && !creds.email.trim())
                }
                className="gap-1.5"
              >
                {validateAndList.isPending ? (
                  <>
                    <CircleDashed className="h-4 w-4 animate-spin" aria-hidden /> Connecting…
                  </>
                ) : (
                  <>
                    Connect <ArrowRight className="h-4 w-4" aria-hidden />
                  </>
                )}
              </Button>
            </>
          ) : step === 'pick' ? (
            <>
              <Button type="button" variant="ghost" onClick={() => setStep('connect')} className="gap-1.5">
                <ArrowLeft className="h-4 w-4" aria-hidden /> Back
              </Button>
              <Button
                type="button"
                onClick={() => setStep('configure')}
                disabled={!selectedProject}
                className="gap-1.5"
              >
                Next <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
            </>
          ) : step === 'configure' ? (
            <>
              <Button type="button" variant="ghost" onClick={() => setStep('pick')} className="gap-1.5">
                <ArrowLeft className="h-4 w-4" aria-hidden /> Back
              </Button>
              <Button
                type="button"
                onClick={() => start.mutate()}
                disabled={
                  start.isPending ||
                  !localName.trim() ||
                  !/^[A-Z][A-Z0-9]{1,9}$/.test(computedKey)
                }
                className="gap-1.5"
              >
                {start.isPending ? (
                  <>
                    <CircleDashed className="h-4 w-4 animate-spin" aria-hidden /> Starting…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" aria-hidden /> Start import
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={jobQuery.data?.status === 'RUNNING' || jobQuery.data?.status === 'PENDING'}
              >
                {jobQuery.data?.status === 'COMPLETED' || jobQuery.data?.status === 'FAILED'
                  ? 'Close'
                  : 'Working…'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepBar({ step }: { step: Step }) {
  const steps: Array<{ id: Step; label: string }> = [
    { id: 'connect', label: 'Connect' },
    { id: 'pick', label: 'Pick project' },
    { id: 'configure', label: 'Configure' },
    { id: 'progress', label: 'Import' },
  ];
  const idx = steps.findIndex((s) => s.id === step);
  return (
    <div className="mt-4 flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s.id} className="flex flex-1 items-center gap-2">
          <div
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ring-1',
              i < idx
                ? 'bg-emerald-500 text-white ring-emerald-500'
                : i === idx
                  ? 'bg-primary text-primary-foreground ring-primary'
                  : 'bg-muted text-muted-foreground ring-border',
            )}
          >
            {i < idx ? <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> : i + 1}
          </div>
          <span
            className={cn(
              'text-xs font-medium',
              i <= idx ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {s.label}
          </span>
          {i < steps.length - 1 ? (
            <span className="flex-1 border-t border-dashed border-border" />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ConnectStep({
  creds,
  setCreds,
  busy,
  onSubmit,
}: {
  creds: JiraCredentialsInput;
  setCreds: (c: JiraCredentialsInput) => void;
  busy: boolean;
  onSubmit: () => void;
}) {
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="rounded-lg border bg-muted/20 p-3 text-xs text-muted-foreground">
        Use a Jira{' '}
        <a
          href="https://id.atlassian.com/manage-profile/security/api-tokens"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
        >
          API token
          <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
        . Credentials are used for this session only — they are not stored.
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <KeyRound className="h-3.5 w-3.5" aria-hidden /> Token type
        </Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setCreds({ ...creds, authMode: 'basic' })}
            aria-pressed={(creds.authMode ?? 'basic') === 'basic'}
            className={cn(
              'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
              (creds.authMode ?? 'basic') === 'basic'
                ? 'border-primary/60 bg-primary/5 ring-2 ring-primary/30'
                : 'border-input hover:bg-muted/40',
            )}
          >
            <div className="font-medium">Classic API token</div>
            <div className="text-[11px] text-muted-foreground">
              No scopes — full account access.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setCreds({ ...creds, authMode: 'bearer' })}
            aria-pressed={creds.authMode === 'bearer'}
            className={cn(
              'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
              creds.authMode === 'bearer'
                ? 'border-primary/60 bg-primary/5 ring-2 ring-primary/30'
                : 'border-input hover:bg-muted/40',
            )}
          >
            <div className="font-medium">Scoped API token</div>
            <div className="text-[11px] text-muted-foreground">
              Bearer auth — needs read scopes.
            </div>
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="jira-base"
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden /> Site URL
        </Label>
        <Input
          id="jira-base"
          required
          autoFocus
          placeholder="your-site.atlassian.net"
          value={creds.baseUrl}
          onChange={(e) => setCreds({ ...creds, baseUrl: e.target.value })}
          disabled={busy}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {(creds.authMode ?? 'basic') === 'basic' ? (
          <div className="space-y-2">
            <Label
              htmlFor="jira-email"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <Mail className="h-3.5 w-3.5" aria-hidden /> Email
            </Label>
            <Input
              id="jira-email"
              type="email"
              required
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              disabled={busy}
            />
          </div>
        ) : null}
        <div className={cn('space-y-2', creds.authMode === 'bearer' ? 'sm:col-span-2' : '')}>
          <Label
            htmlFor="jira-token"
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            <KeyRound className="h-3.5 w-3.5" aria-hidden /> API token
          </Label>
          <Input
            id="jira-token"
            type="password"
            required
            value={creds.apiToken}
            onChange={(e) => setCreds({ ...creds, apiToken: e.target.value })}
            disabled={busy}
          />
        </div>
      </div>

      {creds.authMode === 'bearer' ? (
        <details className="rounded-lg border bg-muted/10 p-3 text-xs">
          <summary className="cursor-pointer font-medium text-foreground">
            Required scopes for the scoped token
          </summary>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
            <li><code>read:me</code> — validate connection</li>
            <li><code>read:project:jira</code>, <code>read:project.property:jira</code> — list projects + statuses</li>
            <li><code>read:issue:jira</code>, <code>read:issue-meta:jira</code>, <code>read:issue-details:jira</code> — read issues</li>
            <li><code>read:comment:jira</code> — read comments</li>
            <li><code>read:user:jira</code> — resolve assignees / reporters</li>
            <li>
              <code>read:board-scope:jira-software</code>, <code>read:sprint:jira-software</code> — sprints
            </li>
            <li>
              For real user emails, the token must be created by a Jira site
              admin and granted <code>manage:jira-configuration</code>;
              otherwise imported users will keep placeholder
              <code>@jira.local</code> emails.
            </li>
          </ul>
        </details>
      ) : null}
    </form>
  );
}

function PickStep({
  projects,
  selected,
  onSelect,
}: {
  projects: JiraProject[];
  selected: JiraProject | null;
  onSelect: (p: JiraProject) => void;
}) {
  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 py-10 text-center">
        <FolderKanban className="h-6 w-6 text-muted-foreground" aria-hidden />
        <p className="text-sm font-medium">No Jira projects visible with these credentials</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {projects.map((p) => {
        const isSel = selected?.id === p.id;
        const initials = p.name.trim().split(/\s+/).slice(0, 2).map((s) => s.charAt(0).toUpperCase()).join('');
        return (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onSelect(p)}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all',
                isSel
                  ? 'border-primary/60 bg-primary/5 ring-2 ring-primary/30'
                  : 'border-input hover:border-primary/40 hover:bg-muted/40',
              )}
            >
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-xs font-bold ring-1',
                  isSel
                    ? 'bg-primary/10 text-primary ring-primary/30'
                    : 'bg-muted text-muted-foreground ring-border',
                )}
              >
                {initials || <FolderKanban className="h-4 w-4" aria-hidden />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-border">
                    {p.key}
                  </span>
                  <span className="truncate text-sm font-semibold">{p.name}</span>
                </div>
                {p.lead?.displayName ? (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Lead: {p.lead.displayName}
                  </div>
                ) : null}
              </div>
              <div
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full ring-1',
                  isSel ? 'bg-primary text-primary-foreground ring-primary' : 'ring-border',
                )}
                aria-hidden
              >
                {isSel ? <CheckCircle2 className="h-4 w-4" /> : null}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ConfigureStep({
  project,
  localName,
  setLocalName,
  computedKey,
  onKeyChange,
  includeComments,
  setIncludeComments,
}: {
  project: JiraProject;
  localName: string;
  setLocalName: (v: string) => void;
  computedKey: string;
  onKeyChange: (v: string) => void;
  includeComments: boolean;
  setIncludeComments: (v: boolean) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="rounded-lg border bg-muted/20 p-3 text-xs text-muted-foreground">
        Importing Jira project{' '}
        <span className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-0.5 font-mono text-[11px] font-semibold ring-1 ring-border">
          {project.key}
        </span>{' '}
        · <span className="font-medium text-foreground">{project.name}</span>
      </div>

      <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
        <div className="space-y-2">
          <Label
            htmlFor="local-name"
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            <Tag className="h-3.5 w-3.5" aria-hidden /> Local project name
          </Label>
          <Input
            id="local-name"
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            className="h-11 text-sm"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label
            htmlFor="local-key"
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            <Hash className="h-3.5 w-3.5" aria-hidden /> Key
          </Label>
          <Input
            id="local-key"
            value={computedKey}
            onChange={(e) => onKeyChange(e.target.value)}
            className="h-11 font-mono text-sm uppercase"
            maxLength={10}
          />
        </div>
      </div>
      <p className="-mt-3 text-[11px] text-muted-foreground">
        2–10 uppercase letters or digits, starting with a letter. Imported issues will get keys
        like <code>{computedKey || 'AB'}-1</code>.
      </p>

      <label className="flex items-start gap-2 rounded-lg border bg-background p-3 text-sm">
        <input
          type="checkbox"
          checked={includeComments}
          onChange={(e) => setIncludeComments(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
        />
        <span>
          <span className="font-medium">Include comments</span>
          <span className="block text-[11px] text-muted-foreground">
            Costs one extra Jira call per issue. Disable for a faster import.
          </span>
        </span>
      </label>
    </div>
  );
}

function ProgressStep({
  job,
  loading,
}: {
  job: ImportJobRow | null;
  loading: boolean;
}) {
  if (loading || !job) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  const pct = job.totalIssues > 0 ? Math.round((job.importedIssues / job.totalIssues) * 100) : 0;
  const warnings = job.warnings?.messages ?? [];
  const running = job.status === 'PENDING' || job.status === 'RUNNING';

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/20 p-4">
        <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <span>Progress</span>
          <span className="tabular-nums text-foreground">
            {job.status === 'RUNNING' || job.status === 'PENDING'
              ? `${pct}%`
              : job.status === 'COMPLETED'
                ? 'Done'
                : 'Failed'}
          </span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              job.status === 'FAILED'
                ? 'bg-rose-500'
                : job.status === 'COMPLETED'
                  ? 'bg-emerald-500'
                  : 'bg-primary',
            )}
            style={{ width: `${job.status === 'COMPLETED' ? 100 : pct}%` }}
          />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
          <Metric label="Issues" value={`${job.importedIssues} / ${job.totalIssues}`} />
          <Metric label="Users" value={String(job.importedUsers)} />
          <Metric label="Sprints" value={`${job.importedSprints} / ${job.totalSprints}`} />
          <Metric label="Comments" value={String(job.importedComments)} />
        </div>
      </div>

      {job.status === 'COMPLETED' && job.project ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-hidden />
          <span>
            Project created:{' '}
            <span className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-0.5 font-mono text-[11px] font-semibold ring-1 ring-border">
              {job.project.key}
            </span>{' '}
            <span className="font-medium">{job.project.name}</span>
          </span>
        </div>
      ) : null}

      {job.status === 'FAILED' && job.error ? (
        <div className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-700 dark:text-rose-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{job.error}</span>
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <details className="rounded-lg border bg-muted/20 p-3 text-xs">
          <summary className="cursor-pointer font-medium text-amber-700 dark:text-amber-300">
            {warnings.length} warning{warnings.length === 1 ? '' : 's'}
          </summary>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-muted-foreground">
            {warnings.slice(0, 20).map((w, i) => (
              <li key={i}>{w}</li>
            ))}
            {warnings.length > 20 ? (
              <li className="italic">…{warnings.length - 20} more</li>
            ) : null}
          </ul>
        </details>
      ) : null}

      {running ? (
        <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <CircleDashed className="h-4 w-4 animate-spin" aria-hidden />
          Importing… this can take a few minutes for large projects.
        </p>
      ) : null}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-background px-2 py-1.5 ring-1 ring-border">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}
